<template>
  <div class="row g-4 align-items-start">
    <Field v-if="isAddressPresent && !isEditingAddress" type="hidden" :name="fieldName('search')" :model-value="search" />
    <div class="col-12 col-lg-8">
      <div class="location-flow">
        <section class="content-panel create-location-panel">
          <div class="panel-header">
            <div>
              <h5 class="panel-title mb-1">Adresse angeben</h5>
              <p class="panel-description mb-0">
                Bitte erfassen Sie hier Ihre Adresse für den Fragebogen.
              </p>
            </div>
          </div>

          <div class="form-group geosearch-wrapper mb-3">
            <label class="form-label" :for="fieldName('search')">Adresse suchen</label>
            <p class="helper-text">
              Geben Sie Straße, Hausnummer und Postleitzahl ein und wählen Sie dann den richtigen Treffer aus.
            </p>
            <template v-if="isEditingAddress">
              <Field
                type="search"
                v-model="search"
                @keyup="getCoords"
                :id="fieldName('search')"
                ref="search"
                :name="fieldName('search')"
                class="form-control"
                :class="{'is-invalid': hasAddressError}"
                autocomplete="off"
                placeholder="Straße Hausnummer PLZ"
              />
            </template>
            <template v-else>
              <button type="button" class="btn btn-secondary btn-sm" @click.prevent="editAddress()">
                Adresse ändern
              </button>
            </template>
            <div v-if="hasAddressError" class="invalid-feedback d-block">
              {{ addressErrorMessage }}
            </div>
            <p v-if="showGeoSearchHint" class="helper-text mb-0">
              Geben Sie mindestens 4 Zeichen ein, um Adressvorschläge zu sehen.
            </p>
            <p v-else-if="showNoGeoResults" class="helper-text helper-text-strong mb-0">
              Keine passende Hausadresse gefunden. Bitte verfeinern Sie die Eingabe.
            </p>
            <div v-if="showDropdown" class="geo-results-section mt-3">
              <div class="section-label">Adressvorschläge</div>
              <div class="geo-results-list" role="list">
                <button
                  v-for="(item, index) in filteredGeoSearchResults"
                  :key="`${item.properties.osm_id}-${index}`"
                  type="button"
                  class="search-result-item geo-result-item"
                  @click="setAddress(index)"
                >
                  <span class="result-name">{{ featureTitle(item) }}</span>
                  <span class="result-address">{{ featureSubtitle(item) }}</span>
                  <span v-if="item.properties.name" class="result-meta">{{ item.properties.name }}</span>
                </button>
              </div>
            </div>
          </div>

          <div v-if="showAddressDetails && isEditingAddress && isAddressPresent" class="address-form">
            <div class="section-label">Adressdaten</div>
            <div class="row g-3">
              <div class="col-12 col-md-6">
                <label :for="fieldName('street')" class="form-label">Straße</label>
                <Field
                  type="text"
                  v-model="address.street"
                  :id="fieldName('street')"
                  :name="fieldName('street')"
                  class="form-control"
                  readonly
                  :class="{'is-invalid': fieldError('street')}"
                  autocomplete="off"
                  placeholder="Straße"
                />
              </div>
              <div class="col-12 col-md-6">
                <label :for="fieldName('zip')" class="form-label">Postleitzahl</label>
                <Field
                  :id="fieldName('zip')"
                  type="text"
                  v-model="address.zip"
                  :name="fieldName('zip')"
                  class="form-control"
                  :class="{'is-invalid': fieldError('zip')}"
                  readonly
                  autocomplete="off"
                  placeholder="Postleitzahl"
                />
              </div>
              <div class="col-12 col-md-6">
                <label :for="fieldName('city')" class="form-label">Ort</label>
                <Field
                  :id="fieldName('city')"
                  type="text"
                  v-model="address.city"
                  :name="fieldName('city')"
                  class="form-control"
                  :class="{'is-invalid': fieldError('city')}"
                  readonly
                  autocomplete="off"
                  placeholder="Ort"
                />
              </div>
              <div class="col-12 col-md-6">
                <label :for="fieldName('country_code')" class="form-label">Ländercode</label>
                <Field
                  :id="fieldName('country_code')"
                  type="text"
                  v-model="address.country_code"
                  :name="fieldName('country_code')"
                  class="form-control"
                  :class="{'is-invalid': fieldError('country_code')}"
                  readonly
                  autocomplete="off"
                  placeholder="DE"
                />
              </div>
            </div>
          </div>
        </section>

        <div v-if="selectedLocationLabel && !isEditingAddress" class="selected-location-summary">
          <div class="section-label selected-location-label">Ausgewählte Adresse</div>
          <div class="selected-location-name">{{ selectedLocationLabel }}</div>
          <div v-if="selectedLocationAddress" class="selected-location-address">{{ selectedLocationAddress }}</div>
        </div>
      </div>
    </div>
    <div class="col-12 col-lg-4 map-column">
      <LeafletLocationMap
        title="Kartenansicht"
        description="Die Karte zeigt die aktuell ausgewählte Adresse."
        :lat="markerLat"
        :lon="markerLon"
      />
    </div>
  </div>
</template>

<script>
import {Field, useFormContext} from 'vee-validate';
import axios from "axios";
import {
  buildPhotonSearchUrl,
  filterPhotonHouseResults,
  formatPhotonFeatureSubtitle,
  formatPhotonFeatureTitle,
  PHOTON_MIN_QUERY_LENGTH,
  PHOTON_SEARCH_DELAY,
  shouldSearchPhoton,
} from "@/scripts/locationSearch";
import LeafletLocationMap from "../survey/parts/LeafletLocationMap.vue";

export default {
  name: "Address",
  components: {
    Field,
    LeafletLocationMap,
  },
  setup() {
    const {setFieldValue, setFieldError, setFieldTouched} = useFormContext()
    return {setFieldValue, setFieldError, setFieldTouched}
  },
  props: {
    errors: Object,
    modelValue: {
      type: Object,
      default: null,
    },
    fieldPrefix: {
      type: String,
      default: '',
    },
    showAddressDetails: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showDropdown: false,
      isEditingAddress: !this.modelValue?.street,
      search: '',
      markerLat: null,
      markerLon: null,
      timer: null,
      searchResult: {
        features: []
      },
      address: {},
    }
  },
  computed: {
    isAddressPresent() {
      return Boolean(this.address?.street)
    },
    hasAddressError() {
      if (this.isEditingAddress || !this.isAddressPresent) {
        return Boolean(this.addressErrorMessage)
      }

      return Boolean(
        this.fieldError('street')
        || this.fieldError('zip')
        || this.fieldError('city')
        || this.fieldError('country_code')
      )
    },
    addressErrorMessage() {
      if (this.isEditingAddress || !this.isAddressPresent) {
        return this.fieldError('search')
          || this.fieldError('street')
          || this.fieldError('zip')
          || this.fieldError('city')
          || this.fieldError('country_code')
          || ''
      }

      return this.fieldError('street')
        || this.fieldError('zip')
        || this.fieldError('city')
        || this.fieldError('country_code')
        || ''
    },
    filteredGeoSearchResults() {
      return filterPhotonHouseResults(this.searchResult.features)
    },
    showGeoSearchHint() {
      return this.isEditingAddress && this.search.length > 0 && this.search.length < PHOTON_MIN_QUERY_LENGTH
    },
    showNoGeoResults() {
      return this.isEditingAddress && this.search.length >= PHOTON_MIN_QUERY_LENGTH && !this.showDropdown && this.filteredGeoSearchResults.length === 0
    },
    selectedLocationLabel() {
      if (this.address?.street) {
        return this.address.street
      }
      return ''
    },
    selectedLocationAddress() {
      if (this.address?.street || this.address?.zip || this.address?.city || this.address?.town) {
        const city = this.address.city || this.address.town
        return [this.address.street, [this.address.zip, city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
      }
      return ''
    },
  },
  methods: {
    fieldName(name) {
      return this.fieldPrefix ? `${this.fieldPrefix}_${name}` : name
    },
    fieldError(name) {
      return this.errors?.[this.fieldName(name)]
    },
    syncSearchToAddress() {
      this.search = this.selectedLocationAddress || this.selectedLocationLabel || ''
    },
    useAddress() {
      this.$emit('update:modelValue', this.address)
    },
    featureTitle(feature) {
      return formatPhotonFeatureTitle(feature)
    },
    featureSubtitle(feature) {
      return formatPhotonFeatureSubtitle(feature)
    },
    setAddress(index, selectedFeature = null) {
      if (!selectedFeature) {
        selectedFeature = this.filteredGeoSearchResults[index]
      }
      this.address.id = null
      this.markerLat = selectedFeature.geometry.coordinates[1]
      this.markerLon = selectedFeature.geometry.coordinates[0]
      this.showDropdown = false
      this.address.street = [selectedFeature.properties.street, selectedFeature.properties.housenumber].filter(v => v).join(' ')
      this.address.zip = selectedFeature.properties.postcode
      this.address.city = selectedFeature.properties.city
      this.address.country_code = selectedFeature.properties.countrycode?.toUpperCase() || 'DE'
      this.address.lat = selectedFeature.geometry.coordinates[1]
      this.address.lon = selectedFeature.geometry.coordinates[0]
      this.syncSearchToAddress()
      this.isEditingAddress = false
      this.useAddress()
      this.$nextTick(() => this.syncAddressValidation())
    },
    editAddress() {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }

      this.isEditingAddress = true
      this.search = ''
      this.searchResult = {features: []}
      this.showDropdown = false
      this.$nextTick(() => this.focusSearch())
    },
    clearAddressValidation() {
      const fields = ['search', 'street', 'zip', 'city', 'country_code']
      fields.forEach((name) => {
        const fullName = this.fieldName(name)
        this.setFieldValue(fullName, '', false)
        this.setFieldError(fullName, undefined)
        this.setFieldTouched(fullName, false)
      })
    },
    syncAddressValidation() {
      if (!this.isAddressPresent) {
        return
      }

      const fields = {
        search: this.search,
        street: this.address.street || '',
        zip: this.address.zip || '',
        city: this.address.city || '',
        country_code: this.address.country_code || '',
      }

      Object.entries(fields).forEach(([name, value]) => {
        this.setFieldValue(this.fieldName(name), value, true)
      })
    },
    getCoords() {
      if (this.timer) {
        clearTimeout(this.timer)
      }

      if (!shouldSearchPhoton(this.search)) {
        this.searchResult = {features: []}
        this.showDropdown = false
        return
      }

      this.timer = setTimeout(() => {
        const query = this.search.trim()
        if (!shouldSearchPhoton(query)) {
          return
        }

        axios.get(buildPhotonSearchUrl(query)).then(({data}) => {
          this.searchResult = data
          this.showDropdown = this.filteredGeoSearchResults.length > 0
          this.focusSearch()
        })
      }, PHOTON_SEARCH_DELAY)
    },
    focusSearch() {
      if (this.$refs.search) {
        this.$refs.search.$el.focus()
      }
    },
  },
  beforeUnmount() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  },
  mounted() {
    if (!this.modelValue?.street) {
      this.$nextTick(() => this.clearAddressValidation())
      return
    }

    this.address = {...this.modelValue}
    if (!this.address.zip && this.address.postcode) {
      this.address.zip = this.address.postcode
    }
    if (!this.address.country_code) {
      this.address.country_code = 'DE'
    }
    if (this.address.latitude && this.address.longitude) {
      this.markerLat = Number(this.address.latitude)
      this.markerLon = Number(this.address.longitude)
    }
    if (this.address.lat && this.address.lon) {
      this.markerLat = Number(this.address.lat)
      this.markerLon = Number(this.address.lon)
    }
    this.syncSearchToAddress()
    this.isEditingAddress = false
    this.$nextTick(() => this.syncAddressValidation())
  },
  watch: {
    modelValue(newValue) {
      if (!newValue?.street) {
        return
      }

      this.address = {...newValue}
      if (!this.address.zip && this.address.postcode) {
        this.address.zip = this.address.postcode
      }
      if (!this.address.country_code) {
        this.address.country_code = 'DE'
      }
      this.syncSearchToAddress()
      this.isEditingAddress = false
      this.$nextTick(() => this.syncAddressValidation())
    },
  }
}
</script>

<style scoped lang="scss">
@import "src/scss/constants";

.location-flow {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .content-panel {
    border: 1px solid #dee2e6;
    background: #ffffff;
    padding: 1rem;
  }

  .panel-header {
    margin-bottom: 1rem;
  }

  .panel-title {
    font-size: 1rem;
    font-weight: 600;
    color: #212529;
  }

  .panel-description,
  .helper-text {
    color: #6c757d;
    font-size: 0.95rem;
    line-height: 1.45;
  }

  .helper-text {
    margin-top: 0.5rem;
  }

  .helper-text-strong {
    color: #495057;
  }

  .section-label {
    margin-bottom: 0.75rem;
    color: #6c757d;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .create-location-panel {
    .helper-text {
      max-width: 48rem;
    }
  }

  .geo-results-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .search-result-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.2rem;
    width: 100%;
    padding: 0.9rem 1rem;
    text-align: left;
    border: 1px solid #dee2e6;
    background-color: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

    &:hover,
    &:focus {
      border-color: $manet_blue_dark;
      box-shadow: 0 0 0 0.2rem rgba(4, 53, 94, 0.12);
      transform: translateY(-1px);
      outline: none;
    }

    .result-name {
      font-weight: 600;
      color: #212529;
    }

    .result-address {
      color: #6c757d;
      font-size: 0.95rem;
    }

    .result-meta {
      color: #495057;
      font-size: 0.85rem;
    }
  }

  .geo-result-item {
    gap: 0.35rem;
  }

  .selected-location-summary {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(31, 122, 85, 0.24);
    border-left: 4px solid #1f7a55;
    background: #eef8f3;
    box-shadow: 0 0.5rem 1.25rem rgba(31, 122, 85, 0.09);
    padding: 1.15rem 1.25rem;

    .selected-location-label {
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
      margin-bottom: 0.65rem;
      padding: 0.3rem 0.65rem 0.3rem 0.4rem;
      background: rgba(31, 122, 85, 0.11);
      color: #176442;
      letter-spacing: 0.045em;

      &::before {
        content: "\2713";
        display: inline-grid;
        width: 1.35rem;
        height: 1.35rem;
        place-items: center;
        border-radius: 50%;
        background: #1f7a55;
        color: #ffffff;
        font-size: 0.78rem;
        line-height: 1;
      }
    }

    .selected-location-name {
      color: #176442;
      font-size: 1.05rem;
      font-weight: 700;
    }

    .selected-location-address {
      color: #6c757d;
      margin-top: 0.2rem;
    }
  }
}

@media (max-width: 991.98px) {
  .location-flow {
    .geo-results-list {
      grid-template-columns: 1fr;
    }
  }
}

@media (min-width: 992px) {
  .map-column {
    align-self: stretch;
  }
}
</style>
